import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { useConfirmation } from '../contexts/ConfirmationContext';
import PageHeader from '../components/ui/PageHeader';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PlatformService } from '../services/platformService';
import { CategoryService } from '../services/categoryService';
import { DossierService } from '../services/dossierService';
import { ScheduleService } from '../services/scheduleService';
import { AuthService } from '../services/authService';
import { FeedbackService } from '../services/feedbackService';
import { Platform, Category, Dossier, User, Schedule, Feedback } from '../types';
import { formatDateTimeGerman } from '../utils/dateFormatter';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';


type AdminTab = 'platforms' | 'users' | 'schedules' | 'feedback';

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirmation();
  const [actionBusy, setActionBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('platforms');
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const loadInFlightRef = useRef(false);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  // const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreatingPlatform, setIsCreatingPlatform] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isCreatingDossier, setIsCreatingDossier] = useState(false);
  const [editingItem, setEditingItem] = useState<{ type: 'platform' | 'category' | 'dossier', item: any } | null>(null);
  const [userSelectionModal, setUserSelectionModal] = useState<{
    isOpen: boolean;
    dayOfWeek: number;
    time: string;
    onConfirm: (userId: string) => void;
  }>({
    isOpen: false,
    dayOfWeek: 0,
    time: '',
    onConfirm: () => {},
  });

  // Form states
  const [platformForm, setPlatformForm] = useState({
    name: '',
    description: '',
    apiEndpoint: '',
    apiKey: '',
    kiApiUrl: '',
    kiApiSecret: '',
    kiApiEnabled: true,
    imageGenerationWebhook: '',
    metadataWebhook: '',
    videoToolsWebhook: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });

  const [dossierForm, setDossierForm] = useState({
    title: '',
    description: '',
    platformId: ''
  });

  // Zustand für Bestätigungsdialoge
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });



  const editingOriginal = editingItem?.type === 'platform' ? platforms.find(item => item.id === editingItem.item.id)
    : editingItem?.type === 'category' ? categories.find(item => item.id === editingItem.item.id)
    : dossiers.find(item => item.id === editingItem?.item.id);
  const editDirty = !!editingItem && JSON.stringify(editingItem.item) !== JSON.stringify(editingOriginal);
  const createDirty = (isCreatingPlatform && Object.entries(platformForm).some(([key, value]) => key !== 'kiApiEnabled' ? !!value : value !== true))
    || (isCreatingCategory && !!(categoryForm.name || categoryForm.description))
    || (isCreatingDossier && !!(dossierForm.title || dossierForm.description));
  const dirty = editDirty || createDirty;
  const clearForms = () => {
    setEditingItem(null); setIsCreatingPlatform(false); setIsCreatingCategory(false); setIsCreatingDossier(false);
    setPlatformForm({ name: '', description: '', apiEndpoint: '', apiKey: '', kiApiUrl: '', kiApiSecret: '', kiApiEnabled: true, imageGenerationWebhook: '', metadataWebhook: '', videoToolsWebhook: '' });
    setCategoryForm({ name: '', description: '' }); setDossierForm({ title: '', description: '', platformId: '' });
  };
  const changeContext = async (action: () => void) => {
    if (actionBusy) return;
    if (dirty && !(await confirm({ title: 'Änderungen verwerfen?', description: 'Die offenen Formulare enthalten noch nicht gespeicherte Änderungen.', confirmLabel: 'Änderungen verwerfen', danger: true }))) return;
    clearForms(); action();
  };
  const closeEdit = () => { void changeContext(() => setEditingItem(null)); };

  // Daten laden
  const loadData = useCallback(async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setIsLoading(true);
    try {
      console.log('Loading platforms...');
      console.log('Loading categories...');
      console.log('Loading dossiers...');
      console.log('Loading users...');
      console.log('Loading schedules...');
      console.log('Loading feedback...');

      const [
        platformsData,
        categoriesData,
        dossiersData,
        usersData,
        schedulesData,
        feedbackData
      ] = await Promise.all([
        PlatformService.getAllPlatforms(),
        CategoryService.getAllCategories(),
        DossierService.getAllDossiers(),
        AuthService.getAllUsers(),
        ScheduleService.getAllSchedules(),
        FeedbackService.getAllFeedback()
      ]);

      console.log('Platforms loaded:', platformsData);
      setPlatforms(platformsData);

      console.log('Categories loaded:', categoriesData);
      setCategories(categoriesData);

      console.log('Dossiers loaded:', dossiersData);
      setDossiers(dossiersData);

      console.log('Users loaded:', usersData);
      setUsers(usersData);

      console.log('Schedules loaded:', schedulesData);
      setSchedules(schedulesData);

      console.log('Feedback loaded:', feedbackData);
      setFeedback(feedbackData); setLoadError(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError(true);
      toast.error('Die Verwaltungsdaten konnten nicht geladen werden. Bitte versuche es erneut.');
    } finally {
      setIsLoading(false);
      loadInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    const isAdmin = !!user?.roles.includes('administrator');
    if (!isAdmin) return;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadData();
  }, [user?.id, user?.roles, loadData]);

  // Bestätigungsdialog öffnen
  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
    });
  };

  // Bestätigungsdialog schließen
  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
    });
  };

  // Prüfen ob Benutzer Administrator ist
  if (!user || !user.roles.includes('administrator')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <PageHeader title={<>Zugriff verweigert</>} description={<>Du hast keine Berechtigung, auf diese Seite zuzugreifen. Nur Administratoren können die Admin-Funktionen nutzen.</>} />
        </div>
      </div>
    );
  }

  // Plattform-Funktionen
  const handleCreatePlatform = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await PlatformService.createPlatform(platformForm);
      setPlatformForm({ 
        name: '', 
        description: '', 
        apiEndpoint: '', 
        apiKey: '', 
        kiApiUrl: '', 
        kiApiSecret: '', 
        kiApiEnabled: true,
        imageGenerationWebhook: '',
        metadataWebhook: '',
        videoToolsWebhook: ''
      });
      setIsCreatingPlatform(false);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error creating platform:', error);
      toast.error('Fehler beim Erstellen der Plattform');
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpdatePlatform = async (platformId: string, updates: Partial<Platform>) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await PlatformService.updatePlatform(platformId, updates);
      setEditingItem(null);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error updating platform:', error);
      toast.error('Fehler beim Aktualisieren der Plattform');
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeletePlatform = async (platformId: string) => {
    showConfirmDialog(
      'Plattform löschen',
      'Die Plattform wird gelöscht. Prüfe vorher, ob sie noch von Artikeln oder Terminen verwendet wird.',
      async () => {
        try {
          await PlatformService.deletePlatform(platformId);
          loadData();
        } catch (error) {
          console.error('Error deleting platform:', error);
          toast.error('Fehler beim Löschen der Plattform');
        }
      }
    );
  };

  // Kategorie-Funktionen
  const handleCreateCategory = async () => {
    if (!selectedPlatform) return;
    if (actionBusy) return;
    setActionBusy(true);
    try {
      console.log('Selected platform:', selectedPlatform);
      console.log('Category form data:', categoryForm);
      const categoryData = {
        ...categoryForm,
        platformId: selectedPlatform.id
      };
      console.log('Final category data:', categoryData);
      await CategoryService.createCategory(categoryData);
      setCategoryForm({ name: '', description: '' });
      setIsCreatingCategory(false);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Fehler beim Erstellen der Kategorie');
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpdateCategory = async (categoryId: string, updates: Partial<Category>) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await CategoryService.updateCategory(categoryId, updates);
      setEditingItem(null);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Fehler beim Aktualisieren der Kategorie');
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    showConfirmDialog(
      'Kategorie löschen',
      'Bist du sicher, dass du diese Kategorie löschen möchtest?',
      async () => {
        try {
          await CategoryService.deleteCategory(categoryId);
          loadData();
        } catch (error) {
          console.error('Error deleting category:', error);
          toast.error('Fehler beim Löschen der Kategorie');
        }
      }
    );
  };

  // Dossier-Funktionen
  const handleCreateDossier = async () => {
    if (!selectedPlatform) return;
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const dossierData = {
        ...dossierForm,
        platformId: selectedPlatform.id
      };
      await DossierService.createDossier(dossierData);
      setDossierForm({ title: '', description: '', platformId: '' });
      setIsCreatingDossier(false);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error creating dossier:', error);
      toast.error('Fehler beim Erstellen des Dossiers');
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpdateDossier = async (dossierId: string, updates: Partial<Dossier>) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await DossierService.updateDossier(dossierId, updates);
      setEditingItem(null);
      toast.success('Änderungen gespeichert.');
      loadData();
    } catch (error) {
      console.error('Error updating dossier:', error);
      toast.error('Fehler beim Aktualisieren des Dossiers');
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteDossier = async (dossierId: string) => {
    showConfirmDialog(
      'Dossier löschen',
      'Bist du sicher, dass du dieses Dossier löschen möchtest?',
      async () => {
        try {
          await DossierService.deleteDossier(dossierId);
          loadData();
        } catch (error) {
          console.error('Error deleting dossier:', error);
          toast.error('Fehler beim Löschen des Dossiers');
        }
      }
    );
  };

  // Feedback-Funktionen
  const handleDeleteFeedback = async (feedbackId: string) => {
    showConfirmDialog(
      'Feedback löschen',
      'Bist du sicher, dass du dieses Feedback löschen möchtest?',
      async () => {
        try {
          await FeedbackService.deleteFeedback(feedbackId);
          loadData();
        } catch (error) {
          console.error('Error deleting feedback:', error);
          toast.error('Fehler beim Löschen des Feedbacks');
        }
      }
    );
  };

  // Termin-Funktionen
  const handleAddSchedule = (dayOfWeek: number, time: string) => {
    if (!selectedPlatform) return;
    
    if (users.length === 0) {
      toast.error('Keine Benutzer verfügbar');
      return;
    }
    
    setUserSelectionModal({
      isOpen: true,
      dayOfWeek,
      time,
      onConfirm: async (userId: string) => {
        const selectedUser = users.find(u => u.id === userId);
        if (!selectedUser) return;
        
        if (actionBusy) return;
        setActionBusy(true);
        try {
          await ScheduleService.createSchedule({
            platformId: selectedPlatform.id,
            authorId: selectedUser.id,
            dayOfWeek,
            time,
            isActive: true
          });
          setUserSelectionModal({ isOpen: false, dayOfWeek: 0, time: '', onConfirm: () => {} });
          loadData();
          toast.success(`Termin erfolgreich erstellt: ${selectedUser.name} - ${ScheduleService.getDayName(dayOfWeek)} ${time}`);
        } catch (error) {
          console.error('Error creating schedule:', error);
          toast.error('Fehler beim Erstellen des Termins');
        } finally { setActionBusy(false); }
      }
    });
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    showConfirmDialog(
      'Termin löschen',
      'Bist du sicher, dass du diesen Termin löschen möchtest?',
      async () => {
        try {
          await ScheduleService.deleteSchedule(scheduleId);
          loadData();
        } catch (error) {
          console.error('Error deleting schedule:', error);
          toast.error('Fehler beim Löschen des Termins');
        }
      }
    );
  };

  // Benutzer-Funktionen
  const handleAssignPlatform = async (userId: string, platformId: string) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await AuthService.assignPlatformToUser(userId, platformId);
      loadData();
      toast.success('Plattform erfolgreich zugewiesen!');
    } catch (error) {
      console.error('Error assigning platform:', error);
      toast.error('Fehler beim Zuweisen der Plattform');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRemovePlatform = async (userId: string, platformId: string) => {
    showConfirmDialog(
      'Plattform-Zuweisung entfernen',
      'Bist du sicher, dass du diese Plattform-Zuweisung entfernen möchtest?',
      async () => {
        try {
          await AuthService.removePlatformFromUser(userId, platformId);
          loadData();
          toast.success('Plattform-Zuweisung erfolgreich entfernt!');
        } catch (error) {
          console.error('Error removing platform:', error);
          toast.error('Fehler beim Entfernen der Plattform-Zuweisung');
        }
      }
    );
  };

  const handleUpdateUserRole = async (userId: string, roles: string[]) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await AuthService.updateUserRole(userId, roles as any);
      loadData();
      toast.success('Benutzer-Rolle erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Fehler beim Aktualisieren der Benutzer-Rolle');
    } finally {
      setActionBusy(false);
    }
  };

  // Filter-Funktionen
  const getCategoriesForPlatform = (platformId: string) => {
    return categories.filter(cat => cat.platformId === platformId);
  };

  // Autorennamen kürzen für bessere Darstellung - nur Nachname
  const shortenAuthorName = (name: string) => {
    if (!name || name.trim() === '') return name;
    
    // Extrahiere alle Teile des Namens
    const parts = name.trim().split(' ');
    
    if (parts.length >= 2) {
      // Letztes Wort ist der Nachname
      return parts[parts.length - 1];
    }
    
    // Fallback: Wenn nur ein Wort vorhanden, das verwenden
    return parts[0];
  };

  // Filtere Autoren ohne Termin für die ausgewählte Plattform
  const getAvailableAuthorsForPlatform = (platformId: string) => {
    if (!platformId) return users;
    
    // Finde alle Autoren, die bereits einen Termin für diese Plattform haben
    const authorsWithSchedule = schedules
      .filter(schedule => schedule.platformId === platformId)
      .map(schedule => schedule.authorId);
    
    // Filtere Autoren heraus, die bereits einen Termin haben
    return users.filter(user => !authorsWithSchedule.includes(user.id));
  };

  const getDossiersForPlatform = (platformId: string) => {
    return dossiers.filter(dossier => dossier.platformId === platformId);
  };

  const getPlatformsForUser = (user: User) => {
    return platforms.filter(platform => user.assignedPlatforms.includes(platform.id));
  };

  const getAvailablePlatformsForUser = (user: User) => {
    return platforms.filter(platform => !user.assignedPlatforms.includes(platform.id));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <UnsavedChangesNotice dirty={dirty} busy={actionBusy} />
      {loadError && <div role="alert" className="card text-red-800">Die Verwaltungsdaten konnten nicht geladen werden. <Button variant="outline" onClick={() => { void changeContext(() => { void loadData(); }); }}>Erneut versuchen</Button></div>}
      <fieldset disabled={actionBusy} className="min-w-0">
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <PageHeader title={<>Administration</>} description={<>Verwalte Plattformen, Kategorien, Dossiers und Benutzer</>} />
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => { void changeContext(() => setActiveTab('platforms')); }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'platforms'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Plattformen & Kategorien
              </button>
              <button
                onClick={() => { void changeContext(() => setActiveTab('users')); }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Benutzer-Verwaltung
              </button>
              <button
                onClick={() => { void changeContext(() => setActiveTab('schedules')); }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'schedules'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Termine
              </button>
              <button
                onClick={() => { void changeContext(() => setActiveTab('feedback')); }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'feedback'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Feedback
              </button>

            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'platforms' && (
              <div className="space-y-6">
                {/* Plattformen */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Plattformen</h2>
                    <Button
                      onClick={() => { void changeContext(() => setIsCreatingPlatform(true)); }}
                      variant="primary"
                      size="sm"
                    >
                      Neue Plattform
                    </Button>
                  </div>

                  {/* Neue Plattform Form */}
                  {isCreatingPlatform && (
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h3 className="text-md font-medium mb-3">Neue Plattform erstellen</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Name"
                          value={platformForm.name}
                          onChange={(e) => setPlatformForm({ ...platformForm, name: e.target.value })}
                          placeholder="z.B. WordPress Blog"
                        />
                        <Input
                          label="API Endpoint"
                          value={platformForm.apiEndpoint}
                          onChange={(e) => setPlatformForm({ ...platformForm, apiEndpoint: e.target.value })}
                          placeholder="https://api.example.com"
                        />
                        <div className="md:col-span-2">
                          <Textarea
                            label="Beschreibung"
                            value={platformForm.description}
                            onChange={(e) => setPlatformForm({ ...platformForm, description: e.target.value })}
                            placeholder="Beschreibung der Plattform..."
                          />
                        </div>
                        <Input
                          label="API Key"
                          value={platformForm.apiKey}
                          onChange={(e) => setPlatformForm({ ...platformForm, apiKey: e.target.value })}
                          placeholder="API-Schlüssel"
                          type="password"
                        />
                        
                        {/* KI-API Einstellungen */}
                        <div className="md:col-span-2 border-t pt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">KI-API Einstellungen</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                              label="KI-API URL"
                              value={platformForm.kiApiUrl}
                              onChange={(e) => setPlatformForm({ ...platformForm, kiApiUrl: e.target.value })}
                              placeholder="https://n8n.koll.cc/webhook/..."
                            />
                            <Input
                              label="KI-API Secret"
                              value={platformForm.kiApiSecret}
                              onChange={(e) => setPlatformForm({ ...platformForm, kiApiSecret: e.target.value })}
                              placeholder="API-Schlüssel für KI-API"
                              type="password"
                            />
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <label className="text-sm font-medium text-gray-700">
                              KI-API aktiviert
                            </label>
                            <button
                              type="button"
                              onClick={() => setPlatformForm({ 
                                ...platformForm, 
                                kiApiEnabled: !platformForm.kiApiEnabled 
                              })}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                platformForm.kiApiEnabled ? 'bg-blue-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  platformForm.kiApiEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                        
                        {/* Webhook Einstellungen */}
                        <div className="md:col-span-2 border-t pt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Webhook Einstellungen</h4>
                          <div className="grid grid-cols-1 gap-4">
                            <Input
                              label="Bildgenerierung Webhook"
                              value={platformForm.imageGenerationWebhook}
                              onChange={(e) => setPlatformForm({ ...platformForm, imageGenerationWebhook: e.target.value })}
                              placeholder="https://n8n.koll.cc/webhook/..."
                            />
                            <Input
                              label="Metadaten Webhook"
                              value={platformForm.metadataWebhook}
                              onChange={(e) => setPlatformForm({ ...platformForm, metadataWebhook: e.target.value })}
                              placeholder="https://n8n.koll.cc/webhook/..."
                            />
                            <Input
                              label="Video-Tools Webhook"
                              value={platformForm.videoToolsWebhook}
                              onChange={(e) => setPlatformForm({ ...platformForm, videoToolsWebhook: e.target.value })}
                              placeholder="https://win.koll.cc/tools/video/..."
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-4">
                        <Button onClick={handleCreatePlatform} variant="primary" size="sm">
                          Erstellen
                        </Button>
                        <Button
                          onClick={() => {
                            setIsCreatingPlatform(false);
                            setPlatformForm({ 
                              name: '', 
                              description: '', 
                              apiEndpoint: '', 
                              apiKey: '', 
                              kiApiUrl: '', 
                              kiApiSecret: '', 
                              kiApiEnabled: true,
                              imageGenerationWebhook: '',
                              metadataWebhook: '',
                              videoToolsWebhook: ''
                            });
                          }}
                          variant="secondary"
                          size="sm"
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Plattformen Liste */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {platforms.map((platform) => (
                      <div
                        key={platform.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedPlatform?.id === platform.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => { void changeContext(() => setSelectedPlatform(platform)); }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium text-gray-900">{platform.name}</h3>
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void changeContext(() => setEditingItem({ type: 'platform', item: platform }));
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlatform(platform.id);
                              }}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{platform.description}</p>
                        <div className="text-xs text-gray-500">
                          {getCategoriesForPlatform(platform.id).length} Kategorien
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ausgewählte Plattform Details */}
                {selectedPlatform && (
                  <div className="border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {selectedPlatform.name} - Kategorien & Dossiers
                      </h2>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => { void changeContext(() => setIsCreatingCategory(true)); }}
                          variant="primary"
                          size="sm"
                        >
                          Neue Kategorie
                        </Button>
                        <Button
                          onClick={() => { void changeContext(() => setIsCreatingDossier(true)); }}
                          variant="primary"
                          size="sm"
                        >
                          Neues Dossier
                        </Button>
                      </div>
                    </div>

                    {/* Neue Kategorie Form */}
                    {isCreatingCategory && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h3 className="text-md font-medium mb-3">Neue Kategorie erstellen</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Name"
                            value={categoryForm.name}
                            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                            placeholder="z.B. Technologie"
                          />
                          
                          <div className="md:col-span-2">
                            <Textarea
                              label="Beschreibung"
                              value={categoryForm.description}
                              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                              placeholder="Beschreibung der Kategorie..."
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2 mt-4">
                          <Button onClick={handleCreateCategory} variant="primary" size="sm">
                            Erstellen
                          </Button>
                          <Button
                            onClick={() => {
                              setIsCreatingCategory(false);
                              setCategoryForm({ name: '', description: '' });
                            }}
                            variant="secondary"
                            size="sm"
                          >
                            Abbrechen
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Neue Dossier Form */}
                    {isCreatingDossier && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h3 className="text-md font-medium mb-3">Neues Dossier erstellen</h3>
                        <div className="grid grid-cols-1 gap-4">
                          <Input
                            label="Titel"
                            value={dossierForm.title}
                            onChange={(e) => setDossierForm({ ...dossierForm, title: e.target.value })}
                            placeholder="z.B. KI & Automatisierung"
                          />
                          <Textarea
                            label="Beschreibung"
                            value={dossierForm.description}
                            onChange={(e) => setDossierForm({ ...dossierForm, description: e.target.value })}
                            placeholder="Beschreibung des Dossiers..."
                          />
                        </div>
                        <div className="flex space-x-2 mt-4">
                          <Button onClick={handleCreateDossier} variant="primary" size="sm">
                            Erstellen
                          </Button>
                          <Button
                            onClick={() => {
                              setIsCreatingDossier(false);
                              setDossierForm({ title: '', description: '', platformId: '' });
                            }}
                            variant="secondary"
                            size="sm"
                          >
                            Abbrechen
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Kategorien */}
                      <div>
                        <h3 className="text-md font-medium text-gray-900 mb-3">Kategorien</h3>
                        <div className="space-y-2">
                          {getCategoriesForPlatform(selectedPlatform.id).map((category) => (
                            <div key={category.id} className="border rounded p-3 bg-white">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-gray-900">{category.name}</h4>
                                  <p className="text-sm text-gray-600">{category.description}</p>
                                  
                                </div>
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => { void changeContext(() => setEditingItem({ type: 'category', item: category })); }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    Bearbeiten
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCategory(category.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Löschen
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {getCategoriesForPlatform(selectedPlatform.id).length === 0 && (
                            <p className="text-gray-500 text-sm">Keine Kategorien vorhanden</p>
                          )}
                        </div>
                      </div>

                      {/* Dossiers */}
                      <div>
                        <h3 className="text-md font-medium text-gray-900 mb-3">Dossiers</h3>
                        <div className="space-y-2">
                          {getDossiersForPlatform(selectedPlatform.id).map((dossier) => (
                            <div key={dossier.id} className="border rounded p-3 bg-white">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-gray-900">{dossier.title}</h4>
                                  <p className="text-sm text-gray-600">{dossier.description}</p>
                                </div>
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => { void changeContext(() => setEditingItem({ type: 'dossier', item: dossier })); }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    Bearbeiten
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDossier(dossier.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Löschen
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {getDossiersForPlatform(selectedPlatform.id).length === 0 && (
                            <p className="text-gray-500 text-sm">Keine Dossiers vorhanden</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Benutzer-Verwaltung</h2>
                  
                  {/* Benutzer Liste */}
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div key={user.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-medium text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <div className="flex space-x-2 mt-1">
                              {user.roles.map((role) => (
                                <span
                                  key={role}
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    role === 'administrator'
                                      ? 'bg-red-100 text-red-800'
                                      : role === 'redakteur'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {role}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                                                              onClick={() => {/* setSelectedUser(user) */}}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Plattformen verwalten
                            </button>
                          </div>
                        </div>

                        {/* Zugewiesene Plattformen */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Zugewiesene Plattformen:</h4>
                          {getPlatformsForUser(user).length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {getPlatformsForUser(user).map((platform) => (
                                <div
                                  key={platform.id}
                                  className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full"
                                >
                                  <span className="text-sm text-blue-800">{platform.name}</span>
                                  <button
                                    onClick={() => handleRemovePlatform(user.id, platform.id)}
                                    className="text-blue-600 hover:text-red-600 text-xs"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Keine Plattformen zugewiesen</p>
                          )}
                        </div>

                        {/* Verfügbare Plattformen */}
                        {getAvailablePlatformsForUser(user).length > 0 && (
                          <div className="mt-3">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Plattform zuweisen:</h4>
                            <div className="flex flex-wrap gap-2">
                              {getAvailablePlatformsForUser(user).map((platform) => (
                                <button
                                  key={platform.id}
                                  onClick={() => handleAssignPlatform(user.id, platform.id)}
                                  className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-sm text-gray-700 transition-colors"
                                >
                                  + {platform.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Rollen-Verwaltung */}
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Rollen verwalten:</h4>
                          <div className="flex space-x-2">
                            {['autor', 'redakteur', 'administrator'].map((role) => (
                              <button
                                key={role}
                                onClick={() => {
                                  const newRoles = user.roles.includes(role as any)
                                    ? user.roles.filter(r => r !== role)
                                    : [...user.roles, role as any];
                                  handleUpdateUserRole(user.id, newRoles);
                                }}
                                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                  user.roles.includes(role as any)
                                    ? role === 'administrator'
                                      ? 'bg-red-100 text-red-800'
                                      : role === 'redakteur'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-800'
                                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                }`}
                              >
                                {user.roles.includes(role as any) ? '✓' : '○'} {role}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'schedules' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Terminplanung</h2>
                  
                  {/* Plattform-Auswahl */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plattform auswählen
                    </label>
                    <select
                      value={selectedPlatform?.id || ''}
                      onChange={(e) => {
                        const platform = platforms.find(p => p.id === e.target.value);
                        void changeContext(() => setSelectedPlatform(platform || null));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Plattform auswählen</option>
                      {platforms.map((platform) => (
                        <option key={platform.id} value={platform.id}>
                          {platform.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Wochenübersicht */}
                  {selectedPlatform && (
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                          Wochenübersicht: {selectedPlatform.name}
                        </h3>
                      </div>
                      
                                             <div className="overflow-x-auto">
                         <table className="min-w-full divide-y divide-gray-200">
                           <thead className="bg-gray-50 sticky top-0 z-10">
                             <tr>
                               <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                 Zeit
                               </th>
                               {Array.from({ length: 7 }, (_, i) => (
                                 <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                   {ScheduleService.getDayName(i)}
                                 </th>
                               ))}
                             </tr>
                           </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {ScheduleService.getAvailableTimes().map((time) => (
                              <tr key={time}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {time}
                                </td>
                                {Array.from({ length: 7 }, (_, dayOfWeek) => {
                                  const schedule = schedules.find(s => 
                                    s.platformId === selectedPlatform.id && 
                                    s.dayOfWeek === dayOfWeek && 
                                    s.time === time
                                  );
                                  const author = schedule ? users.find(u => u.id === schedule.authorId) : null;
                                  
                                  return (
                                    <td key={dayOfWeek} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                      {schedule ? (
                                        <div className="flex items-center gap-1">
                                          <span 
                                            className="text-blue-600 font-medium"
                                            title={author?.name || 'Unbekannter Autor'}
                                          >
                                            {shortenAuthorName(author?.name || 'Unbekannter Autor')}
                                          </span>
                                          <button
                                            onClick={() => handleDeleteSchedule(schedule.id)}
                                            className="text-red-600 hover:text-red-800 text-xs"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ) : (
                                                                                 <button
                                           onClick={() => handleAddSchedule(dayOfWeek, time)}
                                           className="text-gray-400 hover:text-gray-600 text-xs border border-dashed border-gray-300 rounded px-2 py-1 hover:border-gray-400"
                                         >
                                           + Autor
                                         </button>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Benutzer-Feedback</h2>
                  
                  {feedback.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Noch kein Feedback vorhanden.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {feedback.map((item) => (
                        <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">
                                {item.authorName}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {formatDateTimeGerman(item.createdAt)}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteFeedback(item.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Feedback löschen"
                            >
                              ✕
                            </button>
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-800 whitespace-pre-wrap">
                              {item.feedback}
                            </p>
                          </div>
                          
                          {item.articleId && (
                            <div className="mt-3 text-sm text-gray-600">
                              <span className="font-medium">Artikel-ID:</span> {item.articleId}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}


          </div>
        </div>
      </div>

      </fieldset>
      {/* Edit Modal */}
      {editingItem && (
        <Modal title={editingItem.type === 'platform' ? 'Plattform bearbeiten' : editingItem.type === 'category' ? 'Kategorie bearbeiten' : 'Dossier bearbeiten'} onClose={closeEdit} busy={actionBusy}>
          <fieldset disabled={actionBusy} className="space-y-4">
            
            {editingItem.type === 'platform' && (
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={editingItem.item.name}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, name: e.target.value }
                  })}
                />
                <Input
                  label="API Endpoint"
                  value={editingItem.item.apiEndpoint}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, apiEndpoint: e.target.value }
                  })}
                />
                <Textarea
                  label="Beschreibung"
                  value={editingItem.item.description}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, description: e.target.value }
                  })}
                />
                <Input
                  label="API Key"
                  value={editingItem.item.apiKey}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, apiKey: e.target.value }
                  })}
                  type="password"
                />
                
                {/* KI-API Einstellungen */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">KI-API Einstellungen</h4>
                  
                  <div className="space-y-3">
                    <Input
                      label="KI-API URL"
                      value={editingItem.item.kiApiUrl || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        item: { ...editingItem.item, kiApiUrl: e.target.value }
                      })}
                      placeholder="https://n8n.koll.cc/webhook/..."
                    />
                    
                    <Input
                      label="KI-API Secret"
                      value={editingItem.item.kiApiSecret || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        item: { ...editingItem.item, kiApiSecret: e.target.value }
                      })}
                      type="password"
                      placeholder="API-Schlüssel für KI-API"
                    />
                    
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">
                        KI-API aktiviert
                      </label>
                      <button
                        type="button"
                        onClick={() => setEditingItem({
                          ...editingItem,
                          item: { 
                            ...editingItem.item, 
                            kiApiEnabled: !editingItem.item.kiApiEnabled 
                          }
                        })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          editingItem.item.kiApiEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            editingItem.item.kiApiEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Webhook Einstellungen */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Webhook Einstellungen</h4>
                  
                  <div className="space-y-3">
                    <Input
                      label="Bildgenerierung Webhook"
                      value={editingItem.item.imageGenerationWebhook || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        item: { ...editingItem.item, imageGenerationWebhook: e.target.value }
                      })}
                      placeholder="https://n8n.koll.cc/webhook/..."
                    />
                    
                    <Input
                      label="Metadaten Webhook"
                      value={editingItem.item.metadataWebhook || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        item: { ...editingItem.item, metadataWebhook: e.target.value }
                      })}
                      placeholder="https://n8n.koll.cc/webhook/..."
                    />
                    
                    <Input
                      label="Video-Tools Webhook"
                      value={editingItem.item.videoToolsWebhook || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        item: { ...editingItem.item, videoToolsWebhook: e.target.value }
                      })}
                      placeholder="https://win.koll.cc/tools/video/..."
                    />
                  </div>
                </div>
              </div>
            )}

            {editingItem.type === 'category' && (
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={editingItem.item.name}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, name: e.target.value }
                  })}
                />
                
                <Textarea
                  label="Beschreibung"
                  value={editingItem.item.description}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, description: e.target.value }
                  })}
                />
              </div>
            )}

            {editingItem.type === 'dossier' && (
              <div className="space-y-4">
                <Input
                  label="Titel"
                  value={editingItem.item.title}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, title: e.target.value }
                  })}
                />
                <Textarea
                  label="Beschreibung"
                  value={editingItem.item.description}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, description: e.target.value }
                  })}
                />
              </div>
            )}

            <div className="flex space-x-2 mt-6">
              <Button
                onClick={() => {
                  if (editingItem.type === 'platform') {
                    handleUpdatePlatform(editingItem.item.id, editingItem.item);
                  } else if (editingItem.type === 'category') {
                    handleUpdateCategory(editingItem.item.id, editingItem.item);
                  } else if (editingItem.type === 'dossier') {
                    handleUpdateDossier(editingItem.item.id, editingItem.item);
                  }
                }}
                variant="primary"
                size="sm"
              >
                {actionBusy ? 'Wird gespeichert …' : 'Änderungen speichern'}
              </Button>
              <Button
                onClick={closeEdit}
                variant="secondary"
                size="sm"
              >
                Abbrechen
              </Button>
            </div>
          </fieldset>
        </Modal>
      )}

             {/* Benutzer-Auswahl Modal */}
       {userSelectionModal.isOpen && (
         <Modal title={`Benutzer für ${ScheduleService.getDayName(userSelectionModal.dayOfWeek)} ${userSelectionModal.time} auswählen`} onClose={() => setUserSelectionModal({ isOpen: false, dayOfWeek: 0, time: '', onConfirm: () => {} })} busy={actionBusy}>
             <div className="space-y-2 max-h-60 overflow-y-auto">
               {selectedPlatform && getAvailableAuthorsForPlatform(selectedPlatform.id).length === 0 ? (
                 <div className="text-center py-4 text-gray-500">
                   <p>Alle Autoren haben bereits einen Termin für diese Plattform.</p>
                 </div>
               ) : (
                 (selectedPlatform ? getAvailableAuthorsForPlatform(selectedPlatform.id) : users).map((user) => (
                 <button
                   key={user.id}
                   disabled={actionBusy}
                   onClick={() => {
                     userSelectionModal.onConfirm(user.id);

                   }}
                   className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                 >
                   <div className="font-medium text-gray-900">{user.name}</div>
                   <div className="text-sm text-gray-600">{user.email}</div>
                   <div className="flex space-x-1 mt-1">
                     {user.roles.map((role) => (
                       <span
                         key={role}
                         className={`px-2 py-1 text-xs rounded-full ${
                           role === 'administrator'
                             ? 'bg-red-100 text-red-800'
                             : role === 'redakteur'
                             ? 'bg-blue-100 text-blue-800'
                             : 'bg-gray-100 text-gray-800'
                         }`}
                       >
                         {role}
                       </span>
                     ))}
                   </div>
                 </button>
                 ))
               )}
             </div>
             
             <div className="flex justify-end mt-6">
               <Button
                 onClick={() => setUserSelectionModal({ isOpen: false, dayOfWeek: 0, time: '', onConfirm: () => {} })}
                 variant="secondary"
                 size="sm"
               >
                 Abbrechen
               </Button>
             </div>
         </Modal>
       )}

       <ConfirmDialog open={confirmDialog.isOpen} title={confirmDialog.title} description={confirmDialog.message}
         confirmLabel={confirmDialog.title} danger busy={actionBusy} onClose={closeConfirmDialog}
         onConfirm={async () => { if (actionBusy) return; setActionBusy(true); try { await confirmDialog.onConfirm(); closeConfirmDialog(); } finally { setActionBusy(false); } }} />

    </div>
  );
};

export default AdminPage;
